/**
 * Passcode gate. Unlocks the encrypted data blob and seeds the store.
 */
import { store } from '../store.ts';
import { decryptJSON } from '../crypto.ts';
import { saveSummary, loadSummary } from '../storage.ts';
import type { Summary } from '../types.ts';

const TPL = /* html */ `
<style>
:host {
  display: grid;
  place-items: center;
  inset: 0;
  background:
    radial-gradient(ellipse at 50% 30%, oklch(82% 0.10 60 / 0.18), transparent 60%),
    var(--paper);
  view-transition-name: gate;
  position: relative;
  overflow: hidden;
}
:host::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.05;
  background-image: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.92' numOctaves='2' seed='4'/%3E%3CfeColorMatrix values='0 0 0 0 0.32  0 0 0 0 0.18  0 0 0 0 0.12  0 0 0 0.35 0'/%3E%3C/filter%3E%3Crect width='240' height='240' filter='url(%23n)'/%3E%3C/svg%3E");
  mix-blend-mode: multiply;
}
.wrap {
  text-align: center;
  display: grid;
  gap: 24px;
  padding: 32px;
  max-width: 420px;
  position: relative;
  z-index: 1;
}
.bell-mark {
  width: 56px;
  height: 56px;
  margin: 0 auto -8px;
  opacity: 0.85;
  animation: bell-fade 1200ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes bell-fade {
  from { opacity: 0; transform: translateY(8px) scale(0.92); }
  to   { opacity: 0.85; transform: translateY(0) scale(1); }
}
.mark {
  font-family: var(--hindi);
  font-size: clamp(80px, 22vw, 160px);
  font-weight: 400;
  line-height: 0.9;
  letter-spacing: -0.022em;
  background: linear-gradient(180deg, var(--maroon) 0%, var(--maroon-deep) 90%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.en {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-top: -6px;
}
.tag {
  font-family: var(--display);
  font-size: 22px;
  font-style: italic;
  color: var(--ink-dim);
  font-weight: 400;
  margin-block: 8px 16px;
  text-wrap: balance;
}
.field {
  display: grid;
  gap: 10px;
}
input {
  font-family: var(--mono);
  font-size: 18px;
  text-align: center;
  padding: 16px;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 6px;
  color: var(--ink);
  outline: none;
  letter-spacing: 0.42em;
  transition: border-color 180ms cubic-bezier(0.32, 0.72, 0, 1), background 180ms cubic-bezier(0.32, 0.72, 0, 1);
}
input:focus {
  border-color: var(--maroon);
  background: color-mix(in oklch, var(--maroon) 4%, var(--paper-up));
}
input::placeholder { color: var(--ink-faint); letter-spacing: 0.22em; }
button {
  font-family: var(--mono);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.32em;
  padding: 16px;
  background: linear-gradient(180deg, var(--maroon), var(--maroon-deep));
  color: var(--paper);
  border-radius: 6px;
  font-weight: 700;
  transition: transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 120ms;
  border: 1px solid color-mix(in oklch, var(--maroon-deep) 60%, transparent);
}
button:hover { transform: translateY(-1px); }
button:active { transform: scale(0.97); filter: brightness(0.95); }
.hint {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-faint);
  margin-top: 8px;
  letter-spacing: 0.06em;
  text-wrap: pretty;
}
.err {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--rose);
  letter-spacing: 0.06em;
  min-height: 14px;
}
</style>
<div class="wrap">
  <svg class="bell-mark" viewBox="-32 -32 64 64" aria-hidden="true">
    <circle cx="0" cy="0" r="28" fill="none" stroke="oklch(58% 0.13 75 / 0.5)" stroke-width="0.5"/>
    <path d="M -14 -14 Q -14 -22 0 -22 Q 14 -22 14 -14 L 14 6 Q 14 10 11 12 L -11 12 Q -14 10 -14 6 Z"
          fill="none" stroke="oklch(58% 0.13 75)" stroke-width="1.4" stroke-linejoin="round"/>
    <line x1="-18" y1="13" x2="18" y2="13" stroke="oklch(58% 0.13 75)" stroke-width="1.2" stroke-linecap="round"/>
    <circle cx="0" cy="20" r="2.4" fill="oklch(58% 0.13 75)"/>
    <line x1="0" y1="-26" x2="0" y2="-22" stroke="oklch(58% 0.13 75)" stroke-width="1.2" stroke-linecap="round"/>
  </svg>
  <div>
    <div class="mark">डायल</div>
    <div class="en">Dial · the bell rolodex</div>
  </div>
  <div class="tag">Aapka inner circle. Aapka asli network.</div>
  <form class="field" id="form">
    <input id="pass" type="password" placeholder="passcode" autocomplete="off" autocapitalize="off" autocorrect="off" inputmode="text" />
    <button type="submit" id="btn">Unlock</button>
    <div class="err" id="err"></div>
  </form>
  <div class="hint">prototype · passcode in URL hash or remembered locally</div>
</div>
`;

export class DialGate extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const pass = this.#shadow.getElementById('pass') as HTMLInputElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;

    queueMicrotask(() => pass.focus());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const passcode = pass.value.trim();
      if (!passcode) return;
      try {
        const summary = await this.#unlock(passcode);
        await saveSummary(summary);
        sessionStorage.setItem('dial:passcode', passcode);
        this.#transitionToApp(summary);
      } catch {
        err.textContent = 'galat passcode';
        pass.value = '';
        pass.focus();
      }
    });
  }

  async #unlock(passcode: string): Promise<Summary> {
    const cached = await loadSummary();
    if (cached) return cached;
    const res = await fetch('/data.encrypted.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const body = await res.json() as { ciphertext: string };
    return decryptJSON<Summary>(passcode, body.ciphertext);
  }

  #transitionToApp(summary: Summary): void {
    const apply = (): void => {
      store.patch({ phase: 'ready', summary, error: null });
    };
    if ('startViewTransition' in document) {
      (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(apply);
    } else {
      apply();
    }
  }
}

customElements.define('dial-gate', DialGate);
